<template>
  <div v-if="quote" :class="['shortlist-item shortlist-item--large', (sideBySide) ? 'side-by-side' : null]">

    <div class="tabs-container" style="width:100%;">
      <ul class="tabs">
        <li class="tab"
          :class="{'active': (activeQuoteInGroup == index), 'selected': (quoteInGroup.quoteID === selectedQuote.quoteID)}"
          v-for="quoteInGroup, index in quotesInGroup">
          <a href="#" @click.prevent="viewQuoteInGroup(panel, index)" aria-label="View this quote"
            class="tab-link">{{ quoteInGroup.quoteNumber }} <span class="icon-selected"></span></a>
        </li>
        <li style="float:right;list-style:none;display:flex;align-items:center;height:50px;">
          <button class="btn btn-sm btn-danger-light me-2" @click.prevent="removeDerivative()">
            <i class="icon-cross"></i> <span>Remove all quotes for vehicle</span>
          </button>
          <button class="btn btn-sm btn-danger-light" @click="remove(quote)">
            <i class="icon-cross"></i> Remove Quote
          </button>
        </li>
      </ul>
    </div>

    <div class="comparison-content">
      <div style="flex-grow: 1;">
        <single-vehicle
          :quote="quote"
          :averagePencePerMile="averagePencePerMile"
          :showMpgSliderIfApplicable="showMpgSliderIfApplicable"
          :fuelTypeElectricOrPluginHybrid="fuelTypeElectricOrPluginHybrid"
          :horizontalSpec="true"
          :hideEstimatedElectricCosts="true"
          :driverGradesById="driverGradesById"
        />

        <div class="configure-vehicle-container" v-if="shortlist && quote && !quoteExpired">
          <configure-vehicle
            :shortlist="shortlist"
            :quote="quote"
            :colourOptions="colourOptions"
            :trimOptions="trimOptions"
          />
        </div>
      </div>

      <div class="column column-three" style="padding-top:0;padding-bottom:0;">
        <div class="inner-tab-content" style="padding-top:3rem;padding-bottom:3rem;height:100%;min-height:auto; display:flex;">
          <template v-if="quoteExpired">
            <div class="pt-5 text-center">
              <h5>This quote has expired</h5>
              <a href="#" @click="refreshJaamaQuote(quote.quoteID)">Click here to generate a new quote</a>
            </div>
          </template>
          <template v-else>
            <div style="flex-grow:1;">
              <h5 style="font-size:13px;font-weight:bold;text-transform:uppercase;">Monthly Cost</h5>
              <h6 class="mb-5">Figures are based on a Tax Rate of {{ taxRate }}%</h6>
              <div v-if="quote.quote && adminPortalData">
                <ecoamaps-quote-breakdown v-if="route.params.scheme === 'ecoamaps'"
                  :quote="quote.quote"
                  :quoteCosts="costsForSalarySacrificeQuote"
                  :averagePencePerMile="averagePencePerMile"
                  :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
                  :driver-profile="driverProfile"
                  :type="shortlist.type"
                  :show-fuel="true"
                  :show-traditional-cost="true"/>
                <salsac-quote-breakdown v-if="route.params.scheme === 'salsac'"
                  :quote="quote.quote"
                  :quoteCosts="costsForSalarySacrificeQuote"
                  :averagePencePerMile="averagePencePerMile"
                  :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
                  :driver-profile="driverProfile"
                  :type="shortlist.type"
                  :show-fuel="true"
                  :show-traditional-cost="true"/>
                <bch-quote-breakdown v-if="route.params.scheme === 'bch' && driverProfile"
                  :quote="quote.quote"
                  :derivative="quote.derivative"
                  :workingQuote="workingQuote"
                  :averagePencePerMile="averagePencePerMile"
                  :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
                  :driverProfile="driverProfile"
                  :type="shortlist.type"
                  :adminPortalData="adminPortalData"
                />
              </div>
            </div>

            <div>
              <div class="mb-4 with-configuration-estimated-electric" v-if="fuelTypeElectricOrPluginHybrid(quote.derivative.fuelTypeDescription)">
                <estimated-electric-costs
                  class="mb-4"
                  :quote="quote"
                  :averagePencePerMile="averagePencePerMile">
                </estimated-electric-costs>
              </div>
            </div>

            <ul class="actions">
              <li style="width:100%;">
                <button @click="selectQuote(shortlist.type, quote)"
                  :class="['button button--charcoal button--small', (quote.quoteID === selectedQuote.quoteID) ? 'selected' : null, cannotSelectVehicle ? 'button--disabled' : '']"
                  :disabled="cannotSelectVehicle"
                  v-if="colourOptions && trimOptions"
                >
                  <template v-if="!vueTrim || !vueColor">
                    Please select your configuration
                  </template>
                  <template v-else-if="quote.quoteID === selectedQuote.quoteID">
                    Selected
                    <span class="icon-selected"></span>
                  </template>
                  <template v-else>Select This Vehicle</template>
                </button>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </div>

    <div class="column column-four">
      <div class="shortlist-controls" v-if="shortlist.quotes.length">
        <ul class="controls">
          <li class="controls__previous">
            <button @click.prevent="viewPreviousQuoteGroup"><i class="icon-arrow-right-1"></i></button>
          </li>
          <li class="controls__count">{{ currentQuoteGroup + 1 }}/{{ shortlist.quotes.length }}</li>
          <li class="controls__next">
            <button @click.prevent="viewNextQuoteGroup"><i class="icon-arrow-right-1"></i></button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import EstimatedElectricCosts from "@/components/car-comparison/estimated-electric-costs";
import MpgSlider from "@/components/car-comparison/mpg-slider";
import {EventBus} from "@/event-bus";
import EcoamapsQuoteBreakdown from "@/components/car-quote/ecoamaps/quote-breakdown";
import SalsacQuoteBreakdown from "@/components/car-quote/salsac/quote-breakdown";
import BchQuoteBreakdown from "@/components/car-quote/bch/quote-breakdown";
import AdditionalData from '@/components/car-comparison/comparison-additional';
import VehicleImage from "@/components/vehicle-details/vehicle-image";
import ComparisonAdditional from "@/components/car-comparison/comparison-additional";
import VehicleMeta from "@/components/car-comparison/vehicle-meta.vue";
import SideBySide from "@/components/car-comparison/layout/side-by-side.vue";
import SingleVehicle from "@/components/car-comparison/layout/single-vehicle.vue";
import ConfigureVehicle from "@/components/car-comparison/with-configuration/configure-vehicle.vue";
import {fuelTypeElectricOrPluginHybrid} from "@/helpers/fuel-type";
import {useRoute} from "vue-router";

export default {
  name: "bch-comparison-grid-large",
  components: {
    ConfigureVehicle,
    SingleVehicle,
    SideBySide,
    VehicleMeta,
    BchQuoteBreakdown,
    AdditionalData,
    EcoamapsQuoteBreakdown,
    SalsacQuoteBreakdown,
    MpgSlider,
    EstimatedElectricCosts,
    VehicleImage,
    ComparisonAdditional,
    Multiselect,
  },
  props: [
    'adminPortalData',
    "panel",
    "sideBySide",
    "classes",
    "quote",
    "quoteExpired",
    "refreshJaamaQuote",
    "shortlist",
    "session",
    "selectedQuote",
    "viewPreviousQuoteGroup",
    "viewNextQuoteGroup",
    "currentQuoteGroup",
    "activeQuoteInGroup",
    "costsForSalarySacrificeQuote",
    "averagePencePerMile",
    "totalEstimatedElectricCostPerMonth",
    "saveConfigurationInline",
    'driverGradesById',
  ],
  data: () => {
    return {
      defaultMpg: null,
      supportedTerms: [24, 36, 48],
      visibleInnerTab: 'monthlyCosts',
      quoteCosts: null,
      vueColor: false,
      vueTrim: false,
      colourOptions: false,
      trimOptions: false,
      workingQuote: null,
    }
  },
  mounted() {
    this.defaultMpg = (this.quote.derivedMPG ? this.quote.derivedMPG : 30);

    EventBus.$on('trimSelected', (selected) => {
      if (selected.quoteId === this.quote.quoteID) {
        this.vueTrim = selected.value;
        this.updatePrices();
      }
    });

    EventBus.$on('colorSelected', (selected) => {
      if (selected.quoteId === this.quote.quoteID) {
        this.vueColor = selected.value;
        this.updatePrices();
      }
    });

    this.getConfigurationOptions(this.shortlist.shortListID, this.quote.quoteID);
  },

  setup() {
    const route = useRoute();
    return { route }
  },

  watch: {
    quote(newQuote, oldQuote) {
      if (newQuote.quoteID !== oldQuote.quoteID) {
        this.colourOptions = false;
        this.trimOptions = false;
        this.workingQuote = false;
        this.getConfigurationOptions(this.shortlist.shortListID, newQuote.quoteID);
      }
    },
    saveConfigurationInline: function (properties) {
      if (properties.status) {
        this.saveQuoteAndRedirect();
      }
    },
  },
  computed: {
    driverProfile() {
      return this.session.driverProfile
    },
    quotesInGroup() {
      return this.shortlist.quotes[this.currentQuoteGroup];
    },
    taxRate() {
      return this?.session?.driverProfile?.incomeTaxBand?.ratePercent;
    },
    isSalSac() {
      return (this.shortlist.name === 'salsac');
    },
    cannotSelectVehicle() {
      return !this.vueTrim || !this.vueColor;
    },
    showMpgSliderIfApplicable() {
      return !this.shortlist.name === 'bch';
    }
  },
  methods: {
    fuelTypeElectricOrPluginHybrid,
    updatePrices() {
      EventBus.$emit('loadingTitle', 'Retrieving Costs');
      EventBus.$emit('loading', true);

      this.$http.post('/api/configure-vehicle/quote/create-working-quote', {
        quoteId: this.quote.quoteID,
        color: this.vueColor,
        trim: this.vueTrim,
      }).then(response => {
        this.workingQuote = response.data;
        EventBus.$emit('loading', false);
      }).catch(err => {
        EventBus.$emit('loading', false);
        let response = err.response.data;

        this.$notify({
          group: 'notify',
          type: 'error',
          title: 'Error',
          text: response.message
        });
      });
    },

    saveQuoteAndRedirect() {
      EventBus.$emit('loadingTitle', 'Saving Quote');
      EventBus.$emit('loading', true);

      // In the case of side by side view, the "saveConfigurationInline" var is sent to both sides, so check that this is
      //  the "selected" one before saving
      if (this.quote.quoteID === this.selectedQuote.quoteID) {
        this.$http.post('/api/configure-vehicle/quote/store-select', {
          quoteId: this.selectedQuote.quoteID,
          shortlistId: this.shortlist.shortListID,
          color: this.vueColor,
          trim: this.vueTrim,
        })
        .then(response => {
          this.$notify({
            group: 'notify',
            type: 'success',
            title: 'Success',
            text: "Quote saved"
          });

          this.$router.push({
            name: 'Business Contract Hire Personal Details',
            params: {quoteId: response.data.quoteID}
          });
        })
        .catch(err => {
          EventBus.$emit('loading', false);

          let response = err.response.data;

          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response.message
          });
          this.error = err.message.toString();
        });
      }
    },

    getConfigurationOptions(shortListId, quoteId) {
      EventBus.$emit('loadingTitle', 'Fetching configuration options');
      EventBus.$emit('loading', true);

      this.$http.get(`/api/configure-vehicle/shortlist/${shortListId}/quote/${quoteId}`)
        .then(response => {
          EventBus.$emit('loading', false);
          const adminContent = this.adminPortalData.content.products[this.route.params.scheme][0];

          // if a upper price limit is specified then filter the options
          this.colourOptions = adminContent.configure_show_minimal_options && adminContent.configure_paint_upper_price && adminContent.configure_paint_upper_price > 0
            ? response.data.data.colourOptions.filter((option) => option.priceFloat <= Number(adminContent.configure_paint_upper_price))
            : response.data.data.colourOptions;

          this.trimOptions = adminContent.configure_show_minimal_options && adminContent.configure_trim_upper_price && adminContent.configure_trim_upper_price > 0
            ? response.data.data.trimOptions.filter((option) => option.priceFloat <= Number(adminContent.configure_trim_upper_price))
            : response.data.data.trimOptions;
        })
        .catch(err => {
          EventBus.$emit('loading', false);
          let response = err.response.data;

          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response.message
          });
          this.error = err.message.toString();
          this.$router.push({name: response.redirect, params: {...this.$route.params}});
        });
    },

    switchTerm(event) {
      EventBus.$emit('loadingTitle', 'Updating quote');
      EventBus.$emit('loading', true);

      this.$http.post('/api/switch-term/' + this.quote.quoteID, {
        shortlistId: this.shortlist.shortListID,
        term: event.target.value,
        scheme: this.shortlist.name,

      }).then(response => {
        EventBus.$emit('loading', false);
        this.$router.go()
      })
        .catch(err => {
          EventBus.$emit('loading', false);
          let response = err.response;

          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response.message
          });
          this.error = err.message.toString();
          this.$router.push({name: response.redirect, params: {...this.$route.params}});
        });
    },

    viewQuoteInGroup(panel, index) {
      EventBus.$emit('view-quote-in-group', panel, index);
    },

    selectQuote(type, item) {
      this.$emit('select-quote', {
        quote: item,
        quoteID: item.quoteID,
        quoteName: item.quote.definition.quoteName,
        type: type,
        scheme: this.route.params.scheme,
      });
    },

    viewInnerTab(tab) {
      this.visibleInnerTab = tab;
    },

    shortlistItemConfigured(item) {
      // If paintwork or trim aren't set, the vehicle isn't configured yet.
      return !(item.quote.factoryOptionsGrouped['Paintwork'] === undefined ||
        item.quote.factoryOptionsGrouped['Trim'] === undefined);
    },

    getBikRate(shortlistItem) {
      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK1Rate)) {
        return shortlistItem.quote.details.biK1Rate;
      }

      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK2Rate)) {
        return shortlistItem.quote.details.biK2Rate;
      }

      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK3Rate)) {
        return shortlistItem.quote.details.biK3Rate;
      }

      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK4Rate)) {
        return shortlistItem.quote.details.biK4Rate;
      }
    },

    getTraditionalCarCost(shortlistItem) {
      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK1Rate)) {
        return shortlistItem.quote.details.biK1;
      }

      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK2Rate)) {
        return shortlistItem.quote.details.biK2;
      }

      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK3Rate)) {
        return shortlistItem.quote.details.biK3;
      }

      if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK4Rate)) {
        return shortlistItem.quote.details.biK4;
      }
    },

    getBikValue(shortlistItem) {
      if (shortlistItem.quote.taxCalculations) {
        return shortlistItem.quote.taxCalculations.bikTaxAverage;
      }
    },

    getMonthlyVehiclePayment(shortlistItem) {
      const rentalsPayment = Number(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_RentalsPayments);
      const maintenanceCost = Number(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_MaintenanceCost);
      return Number((rentalsPayment + maintenanceCost) / shortlistItem.term).toFixed(2);
    },

    getTotalCosts(shortListItem, type) {
      let total = 0;
      let monthlyPayment = 0;
      let businessFuelCost = 0;
      let privateFuelCost = 0;
      let vehicleInsurance = 0;
      if (!shortListItem.quote.taxCalculations) {
        return false;
      }
      switch (type) {
        case 'bch':
          monthlyPayment = Number(this.getBikValue(shortListItem));
          businessFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostCompanyCar.businessMileage_BusinessFuelCost) / shortListItem.term);
          privateFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostCompanyCar.privateMileage_PrivateFuelCost) / shortListItem.term);
          total = monthlyPayment + businessFuelCost + privateFuelCost;
          break;
        case 'pch':
          monthlyPayment = Number(this.getMonthlyVehiclePayment(shortListItem));
          businessFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.businessMileage_BusinessFuelCost) / shortListItem.term);
          privateFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.privateMileage_PrivateFuelCost) / shortListItem.term);
          vehicleInsurance = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_InsuranceCost) / shortListItem.term);
          total = monthlyPayment + businessFuelCost + privateFuelCost + vehicleInsurance;
          break;
      }
      return Number(total).toFixed(2);
    },

    getCashAllowanceNet(shortlistItem) {
      const cashAllowance = Number(this.getMonthlyCost(Math.abs(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_CashAllowance']), shortlistItem));
      const cashIncomeTax = Number(this.getMonthlyCost(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_IncomeTaxOnCashAlternative'], shortlistItem));
      const cash1NIC = Number(this.getMonthlyCost(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_Class1NICOnCashAllowance'], shortlistItem));
      return Number(cashAllowance - (cashIncomeTax + cash1NIC)).toFixed(2);
    },

    getTotalNetCost(shortlistItem, type) {
      switch (type.toUpperCase()) {
        case 'BCH':
          return this.getMonthlyCost(shortlistItem.quote.taxCalculations.employeeCostCompanyCar.totals_NetPresentValueNPV, shortlistItem);
        case 'PCH':
          return this.getMonthlyCost(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.totals_NetPresentValueNPV, shortlistItem);
      }
    },

    getMonthlyCost(value, shortlistItem) {
      return (Number(value) / shortlistItem.term).toFixed(2);
    },

    removeDerivative() {
      EventBus.$emit('remove-derivative', this.quote.derivativeID);
    },

    remove(quote) {
      const shortListID = this.shortlist.shortListID;
      const quoteID = quote.quoteID;

      EventBus.$emit('loadingTitle', 'Removing Quote');
      EventBus.$emit('loading', true);

      this.$http.post('/api/quotation', {
        method: 'DELETE',
        endpoint: 'shortlists/' + shortListID + '/quotes/' + quoteID,
      })
        .then(response => {

          EventBus.$emit('loading', false);

          if (response.data.status == 200) {

            this.$notify({
              group: 'notify',
              type: 'success',
              title: quote.manufacturerName + ' ' + quote.modelName,
              text: 'Has been removed from your comparison shortlist'
            })

            this.$emit('quote-removed');

            if (this.shortlist.quotes.length === 0) {
              this.$router.push('/origo-retail');
            }

            this.$router.go()

          } else {

            this.$notify({
              group: 'notify',
              type: 'error',
              title: 'Error',
              text: response.data.message
            })

            for (let property in response.data.errors) {
              if (response.data.errors.hasOwnProperty(property)) {
                response.data.errors[property].forEach((error) => {
                  this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: 'Error',
                    text: error
                  })
                });
              }
            }

          }
        })
        .catch(err => {
          EventBus.$emit('loading', false);
          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: err.toString()
          })
          this.error = err.toString()
        });
    },
  }
}
</script>

<style scoped lang="scss">
.comparison-content {
  display: flex;
  flex-direction: row;
  flex-grow: 1;
}

.configure-vehicle-container {
  padding: 1.5rem 3rem 3rem;
}

.side-by-side {
  .comparison-content {
    flex-direction: column;
  }

  .column-3 .inner-tab-content {
    min-height: auto;
  }

  .configure-vehicle-container {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
}
</style>
